<template>
  <DashboardTaskSkeleton :loading="isLoading">
    <v-card variant="outlined">
      <v-toolbar flat>
        <v-toolbar-title>
          <v-icon v-if="!isCalendarView" start @click="displayTasks = !displayTasks">
            mdi-chevron-{{ displayTasks ? 'up' : 'down' }}
          </v-icon>
          <span>{{ title }} ({{ totalRows }})</span>
          <v-tooltip v-if="isCalendarView" location="bottom">
            <template #activator="{ props }">
              <v-icon class="ml-2 mb-1" v-bind="props">mdi-information-outline</v-icon>
            </template>
            <div>
              <div v-for="[key, value] in Object.entries(taskAssigneePrivacyLabels)" :key="key" class="d-flex align-center gap-2">
                <div :style="{ width: '15px', height: '15px', backgroundColor: colorByAssigneeDictionary[key] }" />
                <span>{{ value }}</span>
              </div>
            </div>
          </v-tooltip>
        </v-toolbar-title>
        <v-spacer />
        <template v-if="$vuetify.display.lgAndUp">
          <template v-if="selected.length">
            <v-btn
              icon
              :loading="archivingLoading"
              data-track="task.selection-archive.open"
              @click="showConfirmationDialog = true"
            >
              <v-icon title="Remove Link">
                mdi-trash-can
              </v-icon>
            </v-btn>
            <v-btn
              color="green"
              class="mr-2"
              style="color: #FFFFFF !important;"
              :loading="markCompleteLoading"
              data-track="task.selection.complete"
              @click="updateTasksStatusHandler(taskStatusEnum.completed)"
            >
              <v-icon start>mdi-check</v-icon> Mark Complete
            </v-btn>
            <v-btn
              color="primary"
              class="mr-2"
              :loading="markCompleteLoading"
              data-track="task.selection.reopen"
              @click="updateTasksStatusHandler(taskStatusEnum.pending)"
            >
              <v-icon start>mdi-clock</v-icon> Mark Pending
            </v-btn>
            <v-tooltip location="bottom" :disabled="!hasRecurrentTaskSelected">
              <template #activator="{ props }">
                <div v-bind="props">
                  <v-btn
                    color="grey-darken-2"
                    style="color: #FFFFFF !important;"
                    :loading="rescheduleLoading"
                    :disabled="hasRecurrentTaskSelected"
                    @click="showRescheduleDialog = true"
                  >
                    <v-icon start>mdi-calendar-clock</v-icon> Reschedule
                  </v-btn>
                </div>
              </template>
              <span>You can only reschedule non-recurrent tasks</span>
            </v-tooltip>
          </template>
          <v-spacer />
          <div class="d-flex flex-column">
            <div>Completed tasks</div>
            <v-btn-toggle
              v-model="completedFilter"
              density="compact"
              mandatory
              class="filter-button-group"
            >
              <v-btn :value="FilterAllExcludeFilterEnum.exclude">{{ FilterAllExcludeFilterLabels[FilterAllExcludeFilterEnum.exclude] }}</v-btn>
              <v-btn :value="FilterAllExcludeFilterEnum.all">{{ FilterAllExcludeFilterLabels[FilterAllExcludeFilterEnum.all] }}</v-btn>
              <v-btn :value="FilterAllExcludeFilterEnum.filter">{{ FilterAllExcludeFilterLabels[FilterAllExcludeFilterEnum.filter] }}</v-btn>
            </v-btn-toggle>
          </div>
          <template v-if="!isCalendarView">
            <v-divider vertical class="ml-3 mr-3" />
            <v-checkbox
              v-model="showBeyond"
              class="mt-0"
              hide-details
              label="Beyond 7 days?"
            />
          </template>
        </template>
        <template v-else>
          <template v-if="selected.length">
            <v-menu location="bottom start">
              <template #activator="{ props }">
                <v-btn v-bind="props" icon>
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item data-track="task.selection.complete" @click="updateTasksStatusHandler(taskStatusEnum.completed)">
                  <template #prepend>
                    <v-icon>mdi-check</v-icon>
                  </template>
                  <v-list-item-title>Mark Complete</v-list-item-title>
                </v-list-item>
                <v-list-item data-track="task.selection.reopen" @click="updateTasksStatusHandler(taskStatusEnum.pending)">
                  <template #prepend>
                    <v-icon>mdi-clock</v-icon>
                  </template>
                  <v-list-item-title>Mark Pending</v-list-item-title>
                </v-list-item>
                <v-tooltip location="start" :disabled="!hasRecurrentTaskSelected">
                  <template #activator="{ props }">
                    <div v-bind="props">
                      <v-list-item
                        :disabled="hasRecurrentTaskSelected"
                        @click="showRescheduleDialog = true"
                      >
                        <template #prepend>
                          <v-icon>mdi-calendar-clock</v-icon>
                        </template>
                        <v-list-item-title>Reschedule</v-list-item-title>
                      </v-list-item>
                    </div>
                  </template>
                  <span>You can only reschedule non-recurrent tasks</span>
                </v-tooltip>
                <v-list-item data-track="task.selection-archive.open" @click="showConfirmationDialog = true">
                  <template #prepend>
                    <v-icon>mdi-trash-can</v-icon>
                  </template>
                  <v-list-item-title>Archive</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
          <v-menu
            v-model="menu"
            location="start"
            :close-on-content-click="false"
          >
            <template #activator="{ props }">
              <v-btn icon v-bind="props">
                <v-icon>mdi-filter</v-icon>
              </v-btn>
            </template>
            <v-card min-width="280">
              <v-card-text class="pb-0">
                <div class="subtitle-2 mb-2">Completed tasks</div>
                <v-btn-toggle
                  v-model="completedFilter"
                  mandatory
                  class="filter-button-group d-flex"
                >
                  <v-btn class="flex-grow-1" :value="FilterAllExcludeFilterEnum.exclude">{{ FilterAllExcludeFilterLabels[FilterAllExcludeFilterEnum.exclude] }}</v-btn>
                  <v-btn class="flex-grow-1" :value="FilterAllExcludeFilterEnum.all">{{ FilterAllExcludeFilterLabels[FilterAllExcludeFilterEnum.all] }}</v-btn>
                  <v-btn class="flex-grow-1" :value="FilterAllExcludeFilterEnum.filter">{{ FilterAllExcludeFilterLabels[FilterAllExcludeFilterEnum.filter] }}</v-btn>
                </v-btn-toggle>
                <v-checkbox
                  v-if="!isCalendarView"
                  v-model="showBeyond"
                  class="mt-4"
                  hide-details
                  label="Show beyond 7 days"
                />
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn variant="text" @click="menu = false">Close</v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
        </template>
      </v-toolbar>
      <!-- Same open animation as DashboardCard: the body stays mounted behind
           `v-show` so the expand animates an already laid-out subtree. -->
      <!-- No padding on the animated element itself: v-expand-transition animates its height and
           then drops the inline style, so padding there made the end of open/close jump. The divider
           sits outside the padded body to span the card edge to edge. -->
      <v-expand-transition>
        <div v-show="displayTasks" id="common-data-table">
          <v-divider />
          <div class="tasks-card__body">
            <div
              v-if="showEmptyState"
              class="text-center text-medium-emphasis"
              data-test="tasks-card-empty"
            >
              No tasks found
            </div>
            <TasksTable
              v-show="tasks.length || isCalendarView"
              v-model:calendar-date-start="calendarDateStart"
              v-model:calendar-date-end="calendarDateEnd"
              :tasks="tasks"
              :is-table-loading="isTableLoading"
              :selected="selected"
              :collection-type="collectionType"
              :total-rows="totalRows"
              :associated-records="associatedRecords"
              :is-calendar-view="isCalendarView"
              @fetch:tasks="getTasksWrapper"
              @select:tasks="selectTasksHandler"
              @update:task="updateHandler"
            />
            <RecordTaskCard
              v-if="collectionId"
              :tasks-length="tasks.length"
              :collection-type="collectionType"
              :associated-records="associatedRecords"
            />
          </div>
        </div>
      </v-expand-transition>
    </v-card>
    <ConfirmationDialog
      v-model="showConfirmationDialog"
      @cancel="showConfirmationDialog = false"
      @submit="archiveTasksHandler"
    >
      Are you sure you want to archive selected tasks?
    </ConfirmationDialog>
    <RescheduleTasksDialog
      v-model="showRescheduleDialog"
      :loading="rescheduleLoading"
      @submit="rescheduleTasksHandler"
    />
  </DashboardTaskSkeleton>
</template>

<script>
import { mapState } from 'vuex';
import { intersectionWith } from 'lodash-es';

import { TaskPrivacyEnum } from '@/bundles/Tasks/enums/TaskPrivacyEnum';
import { taskService } from '@/bundles/Tasks/factories/taskServiceFactory';
import { TaskStatusEnum } from '@/bundles/Tasks/enums/TaskStatusEnum';
import { getDefaultTaskPagination } from '@/bundles/Tasks/helpers/defaultPagination';
import { FilterAllExcludeFilterEnum, FilterAllExcludeFilterLabels } from '@/bundles/GroupFilters/enums/transactionType';
import { getTaskStatusFilter } from '@/bundles/Tasks/helpers/helpers';
import { RequestAbortMixin } from '@/bundles/App/mixins/RequestAbortMixin';
import { isRequestCancelledError } from '@/bundles/Http/mappers/ErrorMapper';
import { taskAssigneePrivacyLabels } from '@/bundles/Tasks/consts';
import { colorByAssigneeDictionary } from '@/bundles/Tasks/helpers';
import { useNotifications } from '@/bundles/App/composables/useNotifications';
import { LocalizationEnum } from '@/bundles/Localization/enum/LocalizationEnum';

import DashboardTaskSkeleton from '@/bundles/Skeletons/components/DashboardTaskSkeleton';
import RecordTaskCard from '@/bundles/Tasks/components/RecordTaskCard';
import TasksTable from '@/bundles/Tasks/components/table/TasksTable';
import ConfirmationDialog from '@/bundles/Common/components/ConfirmationDialog.vue';
import RescheduleTasksDialog from '@/bundles/Tasks/components/RescheduleTasksDialog.vue';

export default {
  name: 'TasksCard',

  components: {
    TasksTable,
    RecordTaskCard,
    ConfirmationDialog,
    RescheduleTasksDialog,
    DashboardTaskSkeleton,
  },

  mixins: [RequestAbortMixin],

  props: {
    taskPrivacy: {
      type: String,
      default: TaskPrivacyEnum.me,
    },
    collectionId: {
      type: String,
      default: null
    },
    collectionType: {
      type: String,
      default: null
    },
    associatedRecords: {
      type: Array,
      default: () => []
    },
    hideAssociatedRecordsCard: Boolean,
    marketing: Boolean,
    isCalendarView: Boolean
  },

  emits: ['update:count', 'update:loading'],

  setup () {
    const { addNotification, addSuccessNotification } = useNotifications();

    return { addNotification, addSuccessNotification, LocalizationEnum };
  },

  data: vm => ({
    isLoading: false,
    tasks: [],
    menu: false,
    selected: [],
    completedFilter: FilterAllExcludeFilterEnum.exclude,
    showBeyond: true,
    // Open from the first render, so the expand transition doesn't play on load.
    displayTasks: vm.taskPrivacy === TaskPrivacyEnum.me || vm.isCalendarView,
    markCompleteLoading: false,
    totalRows: 0,
    isTableLoading: false,
    taskStatusEnum: TaskStatusEnum,
    archivingLoading: false,
    showConfirmationDialog: false,
    rescheduleLoading: false,
    showRescheduleDialog: false,
    calendarDateStart: null,
    calendarDateEnd: null,
  }),

  computed: {
    ...mapState('Task', {
      taskItemsPerPage: state => state.tableOptions.itemsPerPage,
    }),
    showEmptyState () {
      // A record's own tasks card has its RecordTaskCard call to action instead.
      return !this.tasks.length && !this.isCalendarView && !this.collectionId;
    },
    hasRecurrentTaskSelected () {
      return this.tasks.some(
        task => this.selected.includes(task._key) && !!task.recurrent_group_id
      );
    },
    title () {
      const taskTitle = {
        [TaskPrivacyEnum.me]: 'My Tasks',
        [TaskPrivacyEnum.by_me]: 'My Assignments',
        [TaskPrivacyEnum.my_team]: `My Team's Tasks`,
      }

      return taskTitle[this.taskPrivacy] || 'Tasks';
    },
  },

  watch: {
    totalRows: {
      handler (value) {
        this.$emit('update:count', value);
      },
      immediate: true,
    },
    isLoading (value) {
      this.$emit('update:loading', value);
    },
    completedFilter: function () {
      this.getTasksWrapper();
    },
    showBeyond: function () {
      this.getTasksWrapper();
    },
    viewTypeModel () {
      this.getTasksWrapper();
    }
  },

  created () {
    this.FilterAllExcludeFilterEnum = FilterAllExcludeFilterEnum;
    this.FilterAllExcludeFilterLabels = FilterAllExcludeFilterLabels;
    this.taskAssigneePrivacyLabels = taskAssigneePrivacyLabels;
    this.colorByAssigneeDictionary = colorByAssigneeDictionary;
  },

  mounted () {
    if (this.collectionId) {
      this.showBeyond = true;
    }

    if (!this.isCalendarView) {
      this.getTasks({ isPaginationChanged: false });
    }

    this.$eventBus.$on('fetch:tasks', this.getTasksWrapper);
  },

  beforeUnmount () {
    this.$eventBus.$off('fetch:tasks', this.getTasksWrapper);
    this.handleRequestAbort();
  },

  methods: {
    async updateTasksStatusHandler (status) {
      try {
        this.markCompleteLoading = true;

        await taskService.updateTasksStatus({
          ids: this.selected,
          status,
        });

        this.selected = [];
        await this.getTasksWrapper();
      } catch (error) {
        this.addNotification({ ...error });
      } finally {
        this.markCompleteLoading = false;
      }
    },
    updateHandler (pagination) {
      this.getTasksWrapper({ isPaginationChanged: true, pagination });
    },
    getTasksWrapper (payload) {
      this.selected = [];
      if (this.isCalendarView) {
        this.getTasksFromCalendar();
        return;
      }

      this.getTasks(payload);
    },
    async getTasksFromCalendar () {
      try {
        if (!this.isCalendarView) return;
        this.isTableLoading = true;

        const statusFilter = getTaskStatusFilter(this.completedFilter);

        const params = {
          status: statusFilter,
          date_start: this.calendarDateStart,
          date_end: this.calendarDateEnd,
        };

        if (this.marketing) {
          params.marketing = true;
        }

        this.handleRequestAbort();
        const { data, total } = await taskService.getTasksForCalendar(params, this.abortController);

        this.tasks = [...data];
        this.totalRows = total;
        this.isTableLoading = false;
      } catch (error) {
        this.addNotification({ ...error, status: 0 });
        if (!isRequestCancelledError(error)) { // // can't be inside finally because of abort controller
          this.isTableLoading = false;
        }
      }
    },
    async getTasks ({ isPaginationChanged = false, pagination = null } = {}) {
      try {
        if (this.isCalendarView) return; // tasks for calendar view are fetched separately

        this[isPaginationChanged ? 'isTableLoading' : 'isLoading'] = true;

        if (!pagination) {
          pagination = getDefaultTaskPagination();
        }

        const statusFilter = getTaskStatusFilter(this.completedFilter);

        const params = {
          assignee_privacy: this.taskPrivacy,
          take: this.taskItemsPerPage,
          sort: pagination.sortBy,
          descending: pagination.descending,
          skip: (pagination.page - 1) * this.taskItemsPerPage,
          status: statusFilter
        };

        if (this.marketing) {
          params.marketing = true;
        }

        if (this.showBeyond) {
          params.beyond_week = true;
        }

        if (this.collectionId) {
          params.collection = this.collectionId;
        }

        this.handleRequestAbort();
        const { data, total } = await taskService.getMyTasks(params, this.abortController);

        this.tasks = [...data];
        this.totalRows = total;
        this[isPaginationChanged ? 'isTableLoading' : 'isLoading'] = false;
      } catch (error) {
        this.addNotification({ ...error, status: 0 });
        if (!isRequestCancelledError(error)) { // // can't be inside finally because of abort controller
          this[isPaginationChanged ? 'isTableLoading' : 'isLoading'] = false;
        }
      }
    },
    selectTasksHandler (taskIds) {
      this.selected = [...taskIds];
    },
    async rescheduleTasksHandler (dueDate) {
      try {
        this.rescheduleLoading = true;

        await taskService.rescheduleTasks({
          task_ids: this.selected,
          due_date: dueDate,
        });

        this.addSuccessNotification('Task rescheduled successfully.');
        this.showRescheduleDialog = false;
        this.selected = [];
        this.getTasksWrapper();
      } catch (error) {
        this.addNotification({ ...error, status: 0 });
      } finally {
        this.rescheduleLoading = false;
      }
    },
    async archiveTasksHandler () {
      try {
        if (!this.selected.length) {
          return;
        }
        this.showConfirmationDialog = false;
        this.archivingLoading = true;

        await taskService.archive({ keys: this.selected });

        const archivedTasks = intersectionWith(this.tasks, this.selected, (task, key) => task._key === key);
        const tasksText = this.$tc(this.LocalizationEnum.task, archivedTasks.length);
        const wasText = this.$tc(this.LocalizationEnum.was, archivedTasks.length);

        this.addNotification({
          title: 'Success',
          status: 200,
          message: `
            ${tasksText} ${archivedTasks.map(task => task.title).join(', ')} ${wasText} archived.
          `,
        });
        this.getTasksWrapper();
        this.selected = [];
      } catch (error) {
        this.addNotification(error);
      } finally {
        this.archivingLoading = false;
      }
    }
  },
};
</script>

<style lang="scss" scoped>
// The table sits inset in its own outline, like the tables in the dashboard
// cards, instead of running into the card's border.
.tasks-card__body {
  padding: 16px;

  :deep(.v-table) {
    border: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 4px;
    overflow: hidden;
  }
}
</style>
